import {
  ConditionalOptionValues,
  ConditionalStepField,
  CreditApplicationFields,
  GroupedConditionalFields,
  renderTemplate,
} from '@dill/dill-shared';
import React, { useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import AppDatePicker, { parseDateValue } from './AppDatePicker';
import { DillCheckbox } from '../../../general/Dill/DillCheckbox';
import { UploadFileInputs } from './UploadFileInput';
import RadioInputs from './RadioInput';
import DocumentField from './DocumentField';
import ConditionalOption from './ConditionalOption';
import StateDropDown from './StateDropDown';
import OptionsDropDown from './OptionsDropDown';
import { parseDropdownOptions } from './conditionalOptionUtils';
import CheckboxList from './CheckboxList';
import { isHTML } from '../../../../utils/helpers';
import moment from 'moment';

interface DynamicFormProps {
  creditApplicationOptionData: Record<string, ConditionalStepField | null | undefined> | null;
  name: string;
  formTemplate?: string | null;
  onChangeData: (data: { value: string; fieldValue: ConditionalOptionValues[] }) => void;
  selectedValueOption: string;
  selectedFieldValue: ConditionalOptionValues[];
  autoPopulateValues?: Record<string, string | number | boolean | null>;
}

export interface DynamicFormRef {
  validate: () => boolean;
}

const DynamicForm = forwardRef<DynamicFormRef, DynamicFormProps>(
  (
    {
      creditApplicationOptionData,
      name,
      formTemplate,
      onChangeData,
      selectedValueOption,
      selectedFieldValue,
      autoPopulateValues,
    },
    ref
  ) => {
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [errors, setErrors] = useState<
      {
        uniqueId: string;
        errorText: string;
      }[]
    >([]);
    const [fetchedConditionalFields, setFetchedConditionalFields] = useState<Record<
      string,
      ConditionalStepField | null
    > | null>(null);
    const didInjectInitialValuesRef = React.useRef(false);
    const selectedFieldValueRef = React.useRef(selectedFieldValue);
    selectedFieldValueRef.current = selectedFieldValue;

    useEffect(() => {
      if (creditApplicationOptionData) {
        const [key, value] = Object.entries(creditApplicationOptionData)[0];
        setSelectedOption(key);
      }
    }, [creditApplicationOptionData]);

    const duplicateWithNextGroupedPosition = (allFields: GroupedConditionalFields[]) => {
      const groupedFields = allFields.filter((f) => f.groupedPosition !== undefined);

      // Find the current max grouped position
      const maxGroupPos = Math.max(...groupedFields.map((f) => f.groupedPosition ?? 0));

      // Get only fields of the last group
      const lastGroupFields = groupedFields.filter((f) => f.groupedPosition === maxGroupPos);

      const nextGroupedPosition = maxGroupPos + 1;

      const newGroup = lastGroupFields.map((field) => {
        // Strip existing `-group-X` suffix
        const cleanFieldId = field.id.replace(/-group-\d+$/, '');
        const clonedFields = field.fields.map((f) => {
          const cleanId = f.id.replace(/-group-\d+$/, '');
          return {
            ...f,
            id: `${cleanId}-group-${nextGroupedPosition}`,
          };
        });

        return {
          ...field,
          id: `${cleanFieldId}-group-${nextGroupedPosition}`,
          groupedPosition: nextGroupedPosition,
          fields: clonedFields,
        };
      });

      return [...allFields, ...newGroup];
    };

    useEffect(() => {
      if (!creditApplicationOptionData) return;

      const updateData: Record<string, ConditionalStepField | null> = {};

      for (const key in creditApplicationOptionData) {
        const option = creditApplicationOptionData[key];
        if (!option) {
          updateData[key] = null;
          continue;
        }

        let updatedOption = option;

        if (option.stepType === 'GROUPED' && option.groupedFields) {
          // Determine required max group from saved values
          let requiredMaxGroup = option.minimunNumberOfGroups
            ? option.minimunNumberOfGroups - 1
            : 0;

          if (selectedValueOption === key && selectedFieldValueRef.current.length > 0) {
            requiredMaxGroup = Math.max(
              ...selectedFieldValueRef.current.map((f) => f.groupedPosition ?? 0)
            );
          }

          let updatedGroupedFields = [...option.groupedFields];

          let currentMaxGroup =
            updatedGroupedFields.length > 0
              ? Math.max(...updatedGroupedFields.map((f) => f.groupedPosition ?? 0))
              : 0;

          while (currentMaxGroup < requiredMaxGroup) {
            updatedGroupedFields = duplicateWithNextGroupedPosition(updatedGroupedFields);

            currentMaxGroup++;
          }

          updatedOption = {
            ...option,
            groupedFields: updatedGroupedFields,
          };
        }

        updateData[key] = updatedOption;
      }

      setFetchedConditionalFields(updateData);
    }, [creditApplicationOptionData, selectedValueOption]);

    useEffect(() => {
      didInjectInitialValuesRef.current = false;
    }, [selectedOption]);

    useEffect(() => {
      if (!fetchedConditionalFields) return;
      if (didInjectInitialValuesRef.current && selectedOption === selectedValueOption) return;

      const groupedInitialData: Record<string, ConditionalOptionValues[]> = {};

      Object.entries(fetchedConditionalFields).forEach(([optionKey, option]) => {
        const optionFields: ConditionalOptionValues[] = [];

        if (!option) return;

        // ---- GROUPED ----
        if (option.stepType === 'GROUPED' && option.groupedFields) {
          option.groupedFields.forEach((group) => {
            group.fields.forEach((field) => {
              const uniqueId =
                `dynamic-form-${optionKey}-${option.groupName}-${group.groupedPosition}-${field.id}`
                  .toLowerCase()
                  .replace(/\s+/g, '-');

              const existingInitial =
                selectedValueOption === optionKey
                  ? selectedFieldValueRef.current?.find((f) => f.uniqueId === uniqueId)
                  : null;

              let value = existingInitial?.value ?? '';
              if (
                field.fromField === 'DATE' &&
                field.defaultValue?.toLowerCase().trim() === 'current'
              ) {
                value = moment().format('MM/DD/YYYY');
              }

              optionFields.push({
                ...field,
                uniqueId,
                id: field.id,
                name: field.name,
                fromField: field.fromField,
                isRequired: field.isRequired,
                isFieldEmail: !!field.isFieldEmail,
                isHashed: !!field.isHashed,
                stepName: option.groupName ?? '',
                stepNumber: option.position ?? 0,
                index: group.index ?? 0,
                groupStepPosition: option.position ?? 0,
                groupName: option.groupName ?? '',
                groupColumnType: group.columnType ?? 'ONE_COLUMN',
                isFieldArray: !!option.isFieldArray,
                groupedPosition: group.groupedPosition ?? 0,
                value,
                optionType: field.optionType,
                secondaryText: field.secondaryText,
                secondaryValue: existingInitial?.secondaryValue ?? null,
                hidden: field.hidden,
                ...(field.linkedFieldId && { linkedFieldId: field.linkedFieldId }),
                ...(field.populateFieldSignatureId && {
                  populateFieldSignatureId: field.populateFieldSignatureId,
                }),
              });
            });
          });
        }

        // ---- NOT GROUPED ----
        if (option.stepType === 'NOT_GROUPED' && option.unGroupedFields) {
          option.unGroupedFields.forEach((field) => {
            const uniqueId = `dynamic-form-${optionKey}-${option.groupName}-0-${field.id}`
              .toLowerCase()
              .replace(/\s+/g, '-');

            const existingInitial =
              selectedValueOption === optionKey
                ? selectedFieldValueRef.current?.find((f) => f.uniqueId === uniqueId)
                : null;
            let value = existingInitial?.value ?? '';
            if (
              field.fromField === 'DATE' &&
              field.defaultValue?.toLowerCase().trim() === 'current'
            ) {
              value = moment().format('MM/DD/YYYY');
            }
            optionFields.push({
              ...field,
              uniqueId,
              id: field.id,
              name: field.name,
              fromField: field.fromField,
              isRequired: field.isRequired,
              isFieldEmail: !!field.isFieldEmail,
              isHashed: !!field.isHashed,
              stepName: option.groupName ?? '',
              stepNumber: option.position ?? 0,
              index: 0,
              groupStepPosition: option.position ?? 0,
              groupName: option.groupName ?? '',
              groupColumnType: option.columnType ?? 'ONE_COLUMN',
              isFieldArray: !!option.isFieldArray,
              groupedPosition: 0,
              value,
              optionType: field.optionType,
              secondaryText: field.secondaryText,
              secondaryValue: existingInitial?.secondaryValue ?? null,
              ...(field.linkedFieldId && { linkedFieldId: field.linkedFieldId }),
              ...(field.populateFieldSignatureId && {
                populateFieldSignatureId: field.populateFieldSignatureId,
              }),
            });
          });
        }

        groupedInitialData[optionKey] = optionFields;
      });

      if (selectedOption && groupedInitialData[selectedOption]) {
        onChangeData({
          value: selectedOption,
          fieldValue: groupedInitialData[selectedOption],
        });
      }

      if (!didInjectInitialValuesRef.current && selectedValueOption) {
        setSelectedOption(selectedValueOption);
        didInjectInitialValuesRef.current = true;
      }
    }, [fetchedConditionalFields]);

    const buildFieldsFromTemplate = (
      optionKey: string,
      update?: { id: string; value: any; secondaryValue?: any }
    ): ConditionalOptionValues[] => {
      if (!fetchedConditionalFields || !fetchedConditionalFields[optionKey]) return [];
      const option = fetchedConditionalFields[optionKey] as ConditionalStepField;
      const existing = selectedFieldValueRef.current || [];
      const fields: ConditionalOptionValues[] = [];

      const applyUpdate = (uid: string, base: ConditionalOptionValues): ConditionalOptionValues => {
        if (!update || uid !== update.id) return base;
        return {
          ...base,
          value: update.value,
          ...(update.secondaryValue !== undefined ? { secondaryValue: update.secondaryValue } : {}),
        };
      };

      if (option.stepType === 'GROUPED' && option.groupedFields) {
        option.groupedFields.forEach((group) => {
          group.fields.forEach((field) => {
            const uid =
              `dynamic-form-${optionKey}-${option.groupName}-${group.groupedPosition}-${field.id}`
                .toLowerCase()
                .replace(/\s+/g, '-');
            const prev = existing.find((f) => f.uniqueId === uid);
            const base: ConditionalOptionValues = {
              ...field,
              uniqueId: uid,
              id: field.id,
              name: field.name,
              fromField: field.fromField,
              isRequired: field.isRequired,
              isFieldEmail: !!field.isFieldEmail,
              isHashed: !!field.isHashed,
              stepName: option.groupName ?? '',
              stepNumber: option.position ?? 0,
              index: group.index ?? 0,
              groupStepPosition: option.position ?? 0,
              groupName: option.groupName ?? '',
              groupColumnType: group.columnType ?? 'ONE_COLUMN',
              isFieldArray: !!option.isFieldArray,
              groupedPosition: group.groupedPosition ?? 0,
              value: prev?.value ?? '',
              optionType: field.optionType,
              secondaryText: field.secondaryText,
              secondaryValue: prev?.secondaryValue ?? null,
              hidden: field.hidden,
              ...(field.linkedFieldId && { linkedFieldId: field.linkedFieldId }),
              ...(field.populateFieldSignatureId && {
                populateFieldSignatureId: field.populateFieldSignatureId,
              }),
            };
            fields.push(applyUpdate(uid, base));
          });
        });
      }

      if (option.stepType === 'NOT_GROUPED' && option.unGroupedFields) {
        option.unGroupedFields.forEach((field) => {
          const uid = `dynamic-form-${optionKey}-${option.groupName}-0-${field.id}`
            .toLowerCase()
            .replace(/\s+/g, '-');
          const prev = existing.find((f) => f.uniqueId === uid);
          const base: ConditionalOptionValues = {
            ...field,
            uniqueId: uid,
            id: field.id,
            name: field.name,
            fromField: field.fromField,
            isRequired: field.isRequired,
            isFieldEmail: !!field.isFieldEmail,
            isHashed: !!field.isHashed,
            stepName: option.groupName ?? '',
            stepNumber: option.position ?? 0,
            index: 0,
            groupStepPosition: option.position ?? 0,
            groupName: option.groupName ?? '',
            groupColumnType: option.columnType ?? 'ONE_COLUMN',
            isFieldArray: !!option.isFieldArray,
            groupedPosition: 0,
            value: prev?.value ?? '',
            optionType: field.optionType,
            secondaryText: field.secondaryText,
            secondaryValue: prev?.secondaryValue ?? null,
            ...(field.linkedFieldId && { linkedFieldId: field.linkedFieldId }),
            ...(field.populateFieldSignatureId && {
              populateFieldSignatureId: field.populateFieldSignatureId,
            }),
          };
          fields.push(applyUpdate(uid, base));
        });
      }

      return fields;
    };

    const onChange = ({
      id,
      value,
      secondaryValue,
    }: {
      id: string;
      value: any;
      secondaryValue?: any;
    }) => {
      if (!selectedOption) return;

      const currentFields = selectedFieldValueRef.current || [];
      const fieldInCurrent = currentFields.some((f) => f.uniqueId === id);

      const updatedFields = fieldInCurrent
        ? currentFields.map((field) =>
            field.uniqueId === id
              ? {
                  ...field,
                  value,
                  ...(secondaryValue !== undefined ? { secondaryValue } : {}),
                }
              : field
          )
        : buildFieldsFromTemplate(selectedOption, { id, value, secondaryValue });

      if (updatedFields.length === 0) return;

      onChangeData({
        value: selectedOption,
        fieldValue: updatedFields,
      });

      setErrors((prev) => prev.filter((e) => e.uniqueId !== id));
    };

    const getColumnsCount = (type: string) =>
      type === 'ONE_COLUMN' ? 1 : type === 'TWO_COLUMNS' ? 2 : 3;

    function groupByGroupedPosition(data: GroupedConditionalFields[]) {
      return data.reduce<Record<number, GroupedConditionalFields[]>>((acc, item) => {
        const position = item.groupedPosition ?? 0;
        if (!acc[position]) {
          acc[position] = [];
        }
        acc[position].push(item);
        return acc;
      }, {});
    }

    const handleValidate = () => {
      if (!selectedOption) return false;

      const currentFields = selectedFieldValue || [];
      const newErrors: { uniqueId: string; errorText: string }[] = [];

      currentFields.forEach((field) => {
        if (field.hidden) return;
        if (field.isRequired) {
          let isEmpty = false;

          if (
            (field.fromField === 'TEXT' ||
              field.fromField === 'TEXT_AREA' ||
              field.fromField === 'OPTION_FIELD') &&
            (!field.value || (typeof field.value === 'string' && field.value.trim() === ''))
          ) {
            isEmpty = true;
          }

          if (
            (field.fromField === 'NUMBER' || field.fromField === 'CURRENCY') &&
            (field.value === null || field.value === '' || isNaN(Number(field.value)))
          ) {
            isEmpty = true;
          }

          if (field.fromField === 'DATE' && !field.value) {
            isEmpty = true;
          }

          if (field.fromField === 'CHECK_BOX' && field.value !== true) {
            isEmpty = true;
          }

          if (field.fromField === 'FILE' && !field.value) {
            isEmpty = true;
          }

          if (field.fromField === 'CONDITIONAL_OPTION' && !field.value) {
            isEmpty = true;
          }

          if (field.fromField === 'STATE_DROPDOWN' && !field.value) {
            isEmpty = true;
          }

          if (isEmpty) {
            newErrors.push({
              uniqueId: field.uniqueId,
              errorText: `${field.name} is required`,
            });
          }
        }
        if (
          field.fromField === 'TEXT' &&
          field.isFieldEmail &&
          field.value &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.toString())
        ) {
          newErrors.push({
            uniqueId: field.uniqueId,
            errorText: `${field.name} must be a valid email.`,
          });
        }
      });

      setErrors(newErrors);

      return newErrors.length === 0;
    };

    useImperativeHandle(ref, () => ({
      validate: handleValidate,
    }));

    const updatedTemplate = useMemo(() => {
      if (selectedFieldValue) {
        const htmlVal = selectedFieldValue.reduce<Record<string, string | number | boolean | null>>(
          (acc, field) => {
            acc[field.id] = field.value;
            return acc;
          },
          {}
        );
        const mergedVal = { ...autoPopulateValues, ...htmlVal };
        const template = renderTemplate(formTemplate ?? '', mergedVal);
        return template;
      }
      return '';
    }, [selectedFieldValue, fetchedConditionalFields, autoPopulateValues]);

    const renderFieldComponent = (
      fields: CreditApplicationFields[],
      groupName: string | null | undefined,
      groupPostion: number
    ) => {
      return fields.map((field, idx) => {
        if (field.hidden) return null;

        let fieldComponent = null;
        const uniqueId = `dynamic-form-${selectedOption ?? ''}-${groupName}-${groupPostion}-${
          field.id || ''
        }`
          .toLowerCase()
          .replace(/\s+/g, '-');
        const fieldData = selectedFieldValue?.find((f) => f.uniqueId === uniqueId);

        let getValue = fieldData?.value;

        const getSecondaryValue = fieldData?.secondaryValue;
        if ((field.fromField === 'NUMBER' || field.fromField === 'CURRENCY') && getValue) {
          const parsedValue = Number(getValue);
          getValue = !isNaN(parsedValue) ? parsedValue : '';
        }

        switch (field.fromField) {
          case 'TEXT':
          case 'NUMBER':
          case 'CURRENCY':
          case 'TEXT_AREA':
            fieldComponent = (
              <AppInputField
                label={field.name}
                inputType={
                  field.fromField === 'TEXT_AREA'
                    ? 'textarea'
                    : field.fromField === 'NUMBER'
                    ? 'number'
                    : 'text'
                }
                value={
                  (field.fromField === 'NUMBER' || field.fromField === 'CURRENCY') &&
                  typeof getValue === 'number'
                    ? getValue
                    : (field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') &&
                      typeof getValue === 'string'
                    ? getValue
                    : field.fromField === 'NUMBER' || field.fromField === 'CURRENCY'
                    ? ''
                    : ''
                }
                isRequired={field.isRequired}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                suffix={field.suffix}
                prefix={field.prefix}
                onBlur={() => {
                  if (
                    (field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') &&
                    typeof getValue === 'string'
                  ) {
                    let val = getValue.trim();
                    if (field.isFieldEmail) {
                      val = val.toLowerCase();
                    }
                    onChange({
                      id: uniqueId,
                      value: val,
                    });
                  }
                }}
                onTextChange={(text) => {
                  onChange({
                    id: uniqueId,
                    value:
                      field.fromField === 'NUMBER' || field.fromField === 'CURRENCY'
                        ? text
                          ? parseFloat(text)
                          : null
                        : text,
                  });
                }}
                labelTextClassName="whitespace-pre-wrap"
                dataPrivate={field?.isHashed ? true : false}
              />
            );
            break;

          case 'DATE':
            fieldComponent = (
              <AppDatePicker
                label={field.name}
                selectedDate={parseDateValue(getValue)}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                isRequired={field.isRequired}
                onDateSelected={(date) => {
                  onChange({
                    id: uniqueId,
                    value: date,
                  });
                }}
              />
            );
            break;

          case 'CHECK_BOX':
            const errorsText = errors.find((e) => e.uniqueId === uniqueId)?.errorText || '';
            fieldComponent = (
              <div>
                <div
                  className={`my-1 flex items-center w-full ${
                    field.alignment === 'RIGHT'
                      ? 'justify-end'
                      : field.alignment === 'CENTER'
                      ? 'justify-center'
                      : 'justify-start'
                  }`}>
                  <DillCheckbox
                    isChecked={typeof getValue === 'boolean' ? getValue : false}
                    onChange={(checked: boolean) => {
                      onChange({ id: uniqueId, value: checked });
                    }}
                  />
                  <div className="ml-2 text-sm">{field.name}</div>
                </div>
                {errorsText && (
                  <p
                    className={`text-2xs ml-2 text-red-500 w-full ${
                      field.alignment === 'RIGHT'
                        ? 'text-end'
                        : field.alignment === 'CENTER'
                        ? 'text-center'
                        : 'text-start'
                    }`}>
                    {errorsText}
                  </p>
                )}
              </div>
            );
            break;

          case 'FILE':
            fieldComponent = (
              <UploadFileInputs
                isRequired={field.isRequired}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                label={field.name}
                labelNode={
                  <span className="flex items-center gap-1">
                    <span>
                      {field.isRequired && <span className="text-red-500">*</span>}
                      {field.name}
                    </span>
                    {field.sampleFile?.url && (
                      <a
                        href={field.sampleFile.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-500 underline">
                        (View sample)
                      </a>
                    )}
                  </span>
                }
                selectedFile={
                  typeof getValue === 'object' && getValue instanceof File ? getValue : null
                }
                selectedFileName={typeof getValue === 'object' ? getValue?.name : null}
                onSelectFile={(data) => {
                  onChange({
                    id: uniqueId,
                    value: data.file,
                  });
                }}
              />
            );
            break;

          case 'OPTION_FIELD':
            fieldComponent = (
              <div className="flex items-center">
                <RadioInputs
                  label={field.name}
                  onChange={(option) => {
                    onChange({
                      id: uniqueId,
                      value: option.value,
                    });
                  }}
                  id={field.name}
                  options={
                    field?.optionList
                      ? field?.optionList.split(',').map((option) => option.trim())
                      : []
                  }
                  optionDescriptions={
                    field?.optionDescriptions
                      ? field.optionDescriptions.split(';;').map((option) => option.trim())
                      : undefined
                  }
                  isRequired={field.isRequired}
                  selectedOption={typeof getValue === 'string' ? getValue : ''}
                  className={field.isRow ? 'gap-2 flex items-center' : ''}
                  errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                  labelTextClassName="whitespace-pre-wrap"
                />
              </div>
            );
            break;

          case 'DOCUMENT_FIELD':
            fieldComponent = (
              <div className="flex items-center w-full">
                <DocumentField
                  text={field.documentText || ''}
                  className={`w-full ${field.fontWeight} text-[${field.fontSize ?? '12'}]px ${
                    field.alignment === 'RIGHT'
                      ? 'text-right'
                      : field.alignment === 'CENTER'
                      ? 'text-center'
                      : 'text-left'
                  }`}
                />
              </div>
            );
            break;

          case 'CONDITIONAL_OPTION':
            fieldComponent = (
              <ConditionalOption
                optionType={field?.optionType ?? ''}
                id={field.id}
                onChange={(data) => {
                  onChange({
                    id: uniqueId,
                    value: data.value,
                    secondaryValue: data.secondaryValue,
                  });
                }}
                isRequired={field.isRequired}
                label={field.name}
                selectedValue={{ value: getValue, secondaryValue: getSecondaryValue }}
                secondaryText={field.secondaryText}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                currentValue={
                  typeof getValue === 'object'
                    ? getValue?.name
                    : typeof getValue === 'string'
                    ? getValue
                    : null
                }
              />
            );
            break;

          case 'STATE_DROPDOWN':
            fieldComponent = (
              <StateDropDown
                isRequired={field.isRequired}
                label={field.name}
                value={typeof getValue === 'string' ? getValue : ''}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                onTextChange={(text) => {
                  onChange({
                    id: uniqueId,
                    value: text,
                  });
                }}
              />
            );
            break;

          case 'CHECKBOX_LIST':
            fieldComponent = (
              <CheckboxList
                isRequired={field.isRequired}
                label={field.name}
                options={
                  field?.optionList
                    ? field?.optionList.split(',').map((option) => option.trim())
                    : []
                }
                value={typeof getValue === 'string' ? getValue : ''}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                onTextChange={(text) => {
                  onChange({
                    id: uniqueId,
                    value: text,
                  });
                }}
              />
            );
            break;

          case 'DROPDOWN':
            fieldComponent = (
              <OptionsDropDown
                isRequired={field.isRequired}
                label={field.name}
                options={parseDropdownOptions(field?.optionList)}
                value={typeof getValue === 'string' ? getValue : ''}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                onTextChange={(text) => {
                  onChange({
                    id: uniqueId,
                    value: text,
                  });
                }}
              />
            );
            break;

          default:
            fieldComponent = (
              <div className="text-sm text-gray-400">{`${field.fromField} to be added`}</div>
            );
            break;
        }

        return (
          <div
            key={idx}
            className={`
              flex-shrink-0
              flex-grow
              basis-[280px]
              sm:basis-[45%]
              lg:basis-[30%]
              max-w-full
            `}>
            <div className="py-2 w-full">{fieldComponent}</div>
          </div>
        );
      });
    };

    const groupedFields = useMemo(() => {
      if (selectedOption && fetchedConditionalFields && fetchedConditionalFields[selectedOption]) {
        return groupByGroupedPosition(
          fetchedConditionalFields?.[selectedOption]?.groupedFields || []
        );
      }
      return [];
    }, [selectedOption, fetchedConditionalFields]);

    return (
      <div>
        <div className="w-full">
          {updatedTemplate && (
            <div>
              {isHTML(updatedTemplate) ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: updatedTemplate,
                  }}
                />
              ) : (
                <p>{updatedTemplate}</p>
              )}
            </div>
          )}
        </div>
        {selectedOption && fetchedConditionalFields && fetchedConditionalFields[selectedOption] && (
          <div>
            {fetchedConditionalFields?.[selectedOption]?.stepType === 'GROUPED' &&
              fetchedConditionalFields?.[selectedOption]?.groupedFields && (
                <div className="rounded-md">
                  {groupedFields &&
                    Object.values(groupedFields).length > 0 &&
                    Object.values(groupedFields).map((groupField, j) => (
                      <div
                        key={
                          `group-${fetchedConditionalFields?.[selectedOption]?.groupName}-${j}` + j
                        }
                        className="w-full GREY_300-BORDER">
                        {groupField.map((field, findex) => {
                          const columns = getColumnsCount(field.columnType);
                          const fieldsArray = Array(columns)
                            .fill(null)
                            .map(
                              (_, idx) =>
                                field.fields[idx] || {
                                  name: '',
                                  id: '',
                                  fromField: 'TEXT',
                                  isRequired: false,
                                  prefix: '',
                                  suffix: '',
                                }
                            );
                          return (
                            <div key={`${field.id}-${findex}`} className="w-full">
                              <div className="flex flex-wrap gap-4 md:gap-2">
                                {renderFieldComponent(
                                  fieldsArray,
                                  fetchedConditionalFields?.[selectedOption]?.groupName,
                                  field.groupedPosition
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                </div>
              )}
            {fetchedConditionalFields?.[selectedOption]?.stepType === 'NOT_GROUPED' &&
              fetchedConditionalFields?.[selectedOption]?.unGroupedFields && (
                <div className="flex flex-wrap gap-4 md:gap-2">
                  {renderFieldComponent(
                    fetchedConditionalFields[selectedOption]?.unGroupedFields ?? [],
                    fetchedConditionalFields[selectedOption]?.groupName,
                    0
                  )}
                </div>
              )}
          </div>
        )}
      </div>
    );
  }
);

export default DynamicForm;
